import { get } from 'lodash-es';
import * as yup from 'yup';
import { ObjectShape } from 'yup';

import { AnyDocument } from '../../../types';
import { NamespacedFormBlockConfig } from '../types/NamespacedFormBlockConfig';
import { buildBlockSchema } from './buildBlockSchema';

export const buildNamespacedShape = (
  configs: NamespacedFormBlockConfig[],
  rootDocument: AnyDocument | undefined,
  values: Record<string, unknown>,
): ObjectShape =>
  Object.fromEntries(
    configs.map((config) => {
      if ('children' in config) {
        return [
          config.namespace,
          yup
            .array()
            .of(
              yup.lazy((itemValue: Record<string, unknown>) => {
                const childShape = buildNamespacedShape(
                  config.children,
                  rootDocument,
                  itemValue,
                );
                return yup.object().shape(childShape);
              }),
            )
            .required(),
        ];
      }

      const { namespace, formFieldBlocks, repeat, getFormValue } = config;

      const buildLazyItemSchema = (itemValue: Record<string, unknown>) =>
        buildBlockSchema(
          formFieldBlocks,
          rootDocument,
          getFormValue
            ? (key) => getFormValue(values, key)
            : (key) => get(itemValue, key),
        );

      if (Array.isArray(repeat)) {
        return [
          namespace,
          yup.object(
            Object.fromEntries(
              repeat.map((key) => [
                key,
                yup.array().of(yup.lazy(buildLazyItemSchema)).required(),
              ]),
            ),
          ),
        ];
      }

      if (repeat === true) {
        return [
          namespace,
          yup.array().of(yup.lazy(buildLazyItemSchema)).required(),
        ];
      }

      const itemSchema = buildBlockSchema(
        formFieldBlocks,
        rootDocument,
        getFormValue
          ? (key) => getFormValue(values, key)
          : (key) => get(values[namespace] as Record<string, unknown>, key),
      );
      return [namespace, itemSchema];
    }),
  );
